import { Box, Chip, FormHelperText, MenuItem, Select, type SelectChangeEvent, Stack } from "@mui/material";

import { type BulkEmailUser } from "../../organisms/BulkEmail/types";

const tRecipientsSelected = "{count} recipients selected";

interface UserMultiSelectProps {
  availableUsers: BulkEmailUser[];
  selectedUsers: BulkEmailUser[];
  onChange: (users: BulkEmailUser[]) => void;
}

export const UserMultiSelect = ({ availableUsers, selectedUsers, onChange }: UserMultiSelectProps): JSX.Element => {
  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const selectedIds = event.target.value as string[];
    const newSelectedUsers = availableUsers.filter((user) => selectedIds.includes(user.id));
    onChange(newSelectedUsers);
  };

  return (
    <Box>
      <Select
        multiple
        fullWidth
        value={selectedUsers.map((user) => user.id)}
        onChange={handleSelectChange}
        renderValue={() => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectedUsers.map((user) => (
              <Chip
                key={user.id}
                label={user.name}
                onDelete={() => {
                  const newSelectedUsers = selectedUsers.filter((u) => u.id !== user.id);
                  onChange(newSelectedUsers);
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                size="small"
                sx={{
                  backgroundColor: "action.selected",
                  fontSize: "0.75rem",
                  height: "24px",
                }}
              />
            ))}
          </Box>
        )}
        sx={{
          "& .MuiSelect-select": {
            maxHeight: "120px",
            minHeight: "24px",
            overflow: "auto",
            p: 1,
          },
        }}
      >
        {availableUsers.map((user) => (
          <MenuItem
            key={user.id}
            value={user.id}
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {user.name} ({user.email})
            </Box>
            <Box
              sx={{
                display: { sm: "block", xs: "none" },
              }}
            >
              <Stack direction="row" spacing={0.5}>
                {user.roles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{
                      backgroundColor: "action.selected",
                      fontSize: "0.75rem",
                      height: "20px",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{tRecipientsSelected.replace("{count}", String(selectedUsers.length))}</FormHelperText>
    </Box>
  );
};
